import { useMemo } from "react";
import KanbanColumn from "./KanbanColumn";
import { PIPELINE_STATES, bucketKeyForPlan } from "./pipelineHelpers";

// Per-state sort rules. The list RPC returns plans in a default newest-
// first order; per the spec we re-sort each column by what's most useful
// for that pipeline stage.
function sortFor(stateKey) {
  switch (stateKey) {
    case "proposed":
      // Oldest first — these are the highest decay risk
      return (a, b) => (asMs(a.proposed_at) - asMs(b.proposed_at)) || 0;
    case "needs_booking":
      // Oldest acceptance first — the patient waiting longest for a
      // booking call is the one front desk should ring next.
      return (a, b) => (asMs(a.accepted_at) - asMs(b.accepted_at)) || 0;
    case "scheduled":
      // Highest value first — locked-in plans, sort by revenue size.
      return (a, b) => (b.total_centavos || 0) - (a.total_centavos || 0);
    case "in_progress":
      // Oldest acceptance first — the patient who's been mid-treatment
      // longest is the one front-desk should chase next
      return (a, b) => (asMs(a.accepted_at) - asMs(b.accepted_at)) || 0;
    case "paused":
    case "ar":
      return (a, b) => (b.days_in_state || 0) - (a.days_in_state || 0);
    case "expired":
      // Oldest expiry first (i.e. plan that expired most recently appears
      // last; the most aged dead plan is on top, signaling cleanup)
      return (a, b) => (asMs(a.proposed_at) - asMs(b.proposed_at)) || 0;
    default:
      // completed, void, draft → most recent first
      return (a, b) => (asMs(b.created_at) - asMs(a.created_at)) || 0;
  }
}

function asMs(iso) {
  if (!iso) return 0;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? 0 : t;
}

export default function PipelineTab({ plans, currency = "PHP", loading, error, onOpen }) {
  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(PIPELINE_STATES.map((s) => [s.key, []]));
    for (const p of plans || []) {
      // bucketKeyForPlan keeps formal plans on their pipeline_state and guards
      // an Ongoing Treatment (is_informal) out of any proposal-side column.
      const key = bucketKeyForPlan(p);
      buckets[key].push(p);
    }
    for (const s of PIPELINE_STATES) {
      buckets[s.key].sort(sortFor(s.key));
    }
    return buckets;
  }, [plans]);

  if (loading) {
    return (
      <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1">
        {PIPELINE_STATES.map((s) => (
          <div
            key={s.key}
            className={[
              "shrink-0 w-[82vw] md:w-[280px] rounded-lg border-2 animate-pulse min-h-[200px]",
              s.column,
            ].join(" ")}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">
        <div className="font-semibold">Couldn’t load pipeline.</div>
        <div className="mt-1 text-rose-700">{String(error.message || error)}</div>
      </div>
    );
  }

  return (
    <div
      data-testid="pipeline-kanban"
      data-tutorial-id="txplan-pipeline-view"
      className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1 snap-x md:snap-mandatory snap-mandatory"
    >
      {PIPELINE_STATES.map((s) => (
        <KanbanColumn key={s.key} state={s} plans={grouped[s.key]} currency={currency} onOpen={onOpen} />
      ))}
    </div>
  );
}
